import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { invariant } from "@searchui-searchforms/shared/utils";
import { placeClearedPubSub } from "@/features/form-fields/basic-fields/placeClearedPubSub";
import { placeDefaultAppliedPubSub } from "@/features/form-fields/basic-fields/placeDefaultAppliedPubSub";
import { placeSelectionPubSub } from "@/features/form-fields/basic-fields/placeSelectionPubSub";
import type { LocationExactnessScope } from "@/entities/place";
import {
    LOCATION_EXACTNESS_SCOPE,
    getCategoryPlaceDataOptions,
    getDefaultPlaceExactness,
    getExactnessButtonLabelKey,
    getExactnessSingleSelectLabelKey,
    getPlaceExactnessOptions,
    useCategoryPlaceDataQuery,
} from "@/entities/place";
import { AboutTheseSettingsLink, SEARCH_TIPS_PLACES_ANCHOR } from "@/shared/components/AboutTheseSettingsLink";
import { useFormAPI, useFormStore } from "@/shared/forms";
import { usePageContextQuery } from "@/shared/page-context";
import { useSubscribe } from "@/shared/utils/useSubscribe";
import { useFieldContext } from "../FieldContext";
import { ExactnessButton } from "./ExactnessButton";
import { ExactnessSingleSelect } from "./ExactnessSingleSelect";

export function PlaceExactness() {
    const { t } = useTranslation();
    const { getFieldValue, setFieldValue } = useFormAPI();
    const { fieldPath, exactnessFieldPath } = useFieldContext();
    const queryClient = useQueryClient();

    invariant(exactnessFieldPath, "exactnessFieldPath is required");

    const { data: pageContext } = usePageContextQuery();
    const { defaultPlaceProximityEnabled } = pageContext.runtimeConfig;
    const { locale } = pageContext;

    const hasLocation = useFormStore((v) => !!get(v.values, `${fieldPath}.location`));
    const gpid = useFormStore(({ values }) => get(values, `${fieldPath}.gpid`)) as string | null | undefined;
    const exactness = useFormStore(({ values }) => get(values, exactnessFieldPath)) as
        | LocationExactnessScope
        | null
        | undefined;

    const { data } = useCategoryPlaceDataQuery(gpid);
    const place = data?.[0];

    const value = exactness || null;
    const options = useMemo(() => (place ? getPlaceExactnessOptions(place.LevelId, place.IdHierarchy) : []), [place]);

    const translatedOptions = useMemo(
        () =>
            options.map((opt) => ({
                ...opt,
                label: t(opt.labelKey),
                fullLabel: t(opt.fullLabelKey),
            })),
        [options, t],
    );

    const selectedOption = options.find((option) => option.value === value) ?? null;
    const singleSelectLabelKey = getExactnessSingleSelectLabelKey(
        place?.LevelId ?? null,
        selectedOption?.fullLabelKey ?? null,
    );

    // Reacts to a genuine place selection published by PlaceAutocompleteField, filtered
    // to this field's own path. useSubscribe wraps this in useEffectEvent, so it always
    // reads fresh closure state with no dependency array to maintain.
    const handlePlaceSelected = async (publishedFieldPath: string, selectedGpid: string): Promise<void> => {
        if (publishedFieldPath !== fieldPath) return;

        // Control arm: preserve legacy behavior — reset to ThisPlace when a value exists.
        if (!defaultPlaceProximityEnabled) {
            const currentExactness = getFieldValue(exactnessFieldPath) as LocationExactnessScope | null;
            if (currentExactness) {
                setFieldValue(exactnessFieldPath, LOCATION_EXACTNESS_SCOPE.ThisPlace);
            }
            return;
        }

        // Test arm: fetch the place, then apply the level-based default.
        const places = await queryClient.ensureQueryData(
            getCategoryPlaceDataOptions({ gpid: selectedGpid, culture: locale }),
        );
        const selectedPlace = places[0];
        if (!selectedPlace) return;

        // Stale-write guard: re-check current form state before writing. If the gpid
        // moved on again (or was cleared) while the fetch was in flight, this write
        // is for a selection that no longer applies. Form state may hold a raw
        // number (autocomplete item Id) while the channel always carries a string,
        // so compare as strings.
        const currentGpid = getFieldValue(`${fieldPath}.gpid` as typeof fieldPath) as
            | string
            | number
            | null
            | undefined;
        if (String(currentGpid ?? "") !== selectedGpid) return;

        setFieldValue(exactnessFieldPath, getDefaultPlaceExactness(selectedPlace.LevelId, selectedPlace.IdHierarchy));

        // Announce that the level-based default has been applied for this selection. This is
        // the completion signal downstream reconcilers wait for (e.g. match-all-terms forcing
        // ThisPlace), so they can act after this write without depending on its timing.
        placeDefaultAppliedPubSub.publish(fieldPath, selectedGpid);
    };

    useSubscribe(placeSelectionPubSub, handlePlaceSelected);

    // Reacts to the gpid being cleared by a text edit (never a selection). A stored
    // scope that carried a level is relative to an IdHierarchy that no longer exists
    // once gpid is null, so collapse it to the plain "exact to this place" scope. A
    // scope that was already Broad (null) is left untouched — see
    // "Exactness outlives gpid" in the design doc.
    const handlePlaceCleared = (publishedFieldPath: string): void => {
        if (publishedFieldPath !== fieldPath) return;

        const currentExactness = getFieldValue(exactnessFieldPath) as LocationExactnessScope | null;
        if (currentExactness) {
            setFieldValue(exactnessFieldPath, LOCATION_EXACTNESS_SCOPE.ThisPlace);
        }
    };

    useSubscribe(placeClearedPubSub, handlePlaceCleared);

    if (!hasLocation) return null;

    if (options.length < 2) {
        return (
            <ExactnessButton
                isExact={!!value}
                onClick={() => {
                    setFieldValue(exactnessFieldPath, value ? null : LOCATION_EXACTNESS_SCOPE.ThisPlace);
                }}
                testId={exactnessFieldPath}
            >
                {t(getExactnessButtonLabelKey(place?.LevelId ?? null))}
            </ExactnessButton>
        );
    }

    return (
        <ExactnessSingleSelect
            value={value}
            options={translatedOptions}
            onChange={(nextValue) => {
                setFieldValue(exactnessFieldPath, nextValue);
            }}
            label={t(singleSelectLabelKey)}
            testId={exactnessFieldPath}
            footer={<AboutTheseSettingsLink anchor={SEARCH_TIPS_PLACES_ANCHOR} />}
        />
    );
}
